import React, { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useSelector } from 'react-redux';
import store, { useAppDispatch } from '../../store';
import { RootState } from '../../rootReducer';
import Login from '../Login/index';
import { getCurrentGuestProfile } from '../../features/auth/auth';
import 'firebase/auth';
import 'firebase/analytics';
import { message, Spin } from 'antd';
import { LoadingIcon } from '../components/Spinner';
import * as Sentry from '@sentry/react';
import useQuery from '../../hooks/useQuery';
import RestaurantFeed from '../Feed/RestaurantFeed/FeedList';
import MenuRouter from '../Menu';
import Checkouts from '../Checkouts';
import { setLastTableNumber } from '../../features/hybrid/hybrid';
import {
  changeOrderType,
  clearCart,
  setQR,
  setQRMenuIds,
  setSubLinkId,
} from '../../features/cart/cart';
import { DEFAULT_THEME } from '../../themes';
import { applyTheme, applyWithThemeObject, extend } from '../../themes/utils';
import {
  fetchQRCodeAddress,
  setCategorizedView,
  setMenusOnly,
  setQRAddress,
  setQRCodeOrderingStatus,
} from '../../features/restaurant/restaurant';
import { GuestHome } from '../Guest';
import { GuestAccessPoint } from '../Guest/GuestAccessPoint';
import manifest from '../manifest.json';
import { RGBAToHexA } from '../../utils';
import { TTRestaurantProfile } from '../../interfaces/restaurant';
import { SavedProductsIndex } from '../SavedProducts';
import TrackingProvider from '../Providers/TrackingProvider';
import { qrLinksApi } from '../../api/qrLinks';
import {
  popToNode,
  pushNode,
  resetStackToRoot,
  setLinksForQr,
} from '../../features/linkedNav/linkedNav';

Sentry.init({
  dsn:
    'https://7acf435d176b46d8b448ce0fa529f468@o440089.ingest.sentry.io/5467875',
  release: 'pwa@' + process.env.REACT_APP_VERSION,
  tracesSampleRate: 0.1,
  environment: 'production',
});

declare global {
  interface Window {
    heap: any;
  }
}

const setting = {
  color: {
    theme: '#000',
  },
  launcher: {
    chatLabel: {
      'en-US': 'Help Desk',
    },
  },
};

function AppRouteComponent(props: {
  spinning: boolean;
  onLoad: () => void;
  render: (props) => React.ReactNode;
  render1: (props) => React.ReactNode;
  render2: () => React.ReactNode;
  render3: () => React.ReactNode;
}) {
  const profiles = useSelector<RootState, TTRestaurantProfile[]>(
    state => state.restaurant.all_profiles,
  );
  return (
    <>
      {/* <Zendesk zendeskKey={zendeskKey} {...setting} /> */}
      <TrackingProvider profiles={profiles ?? []}>
        <Spin indicator={LoadingIcon} spinning={props.spinning}>
          {props.spinning && (
            <div className="justify-center h-full w-full py-1/2 bg-background" />
          )}
          {!props.spinning && (
            <div className="bg-background min-h-screen">
              <Routes>
                <Route path="/checkout/*" element={<Checkouts />} />
                <Route path="/login" element={<Login />} />
                <Route path="/guests/*" element={<GuestHome />} />
                <Route
                  path="/saved-products/*"
                  element={<SavedProductsIndex />}
                />
                <Route
                  path={'/feed/restaurants/:r_id/*'}
                  element={<MenuRouter />}
                />
                <Route
                  path="/feed/restaurants/all"
                  element={<RestaurantFeed />}
                />
                {/* <Route path="/feed/*" children={props.render1} /> */}
                <Route path="/*" element={<RestaurantFeed />} />
              </Routes>
              <GuestAccessPoint />
            </div>
          )}
        </Spin>
      </TrackingProvider>
    </>
  );
}

function AppRoute(props) {
  const [loading, setLoading] = useState(false);
  const isLoggedIn = useSelector((state: RootState) => state.auth.isLoggedIn);
  const dispatch = useAppDispatch();
  const profile = useSelector<RootState, TTRestaurantProfile | null>(
    state => state.restaurant.profile,
  );
  const query = useQuery();
  const [rUrl, setRUrl] = useState<string | null>(null);

  const [theme, setTheme] = useState(DEFAULT_THEME);

  /**
   * Run the applyTheme function every time the theme state changes
   */
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    let values = ['16', '32'];
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      // @ts-ignore
      link.rel = 'icon';
      document.getElementsByTagName('head')[0].appendChild(link);
    }

    if (/(.*)(tabletab\.io)/.test(window.location.hostname)) {
      // @ts-ignore
      link.href = `tt-favicon.ico`;
      let title = document.querySelector('title');
      // @ts-ignore
      title.textContent = 'TableTab | Mobile Order & Payment';
      // @ts-ignore
      let description = document.querySelector('meta[name="description"]');
      // @ts-ignore
      description.content = 'Mobile Order & Payment powered by TableTab.';

      values.forEach(v => {
        let _link = document.createElement('link');
        // @ts-ignore
        _link.rel = 'icon';
        document.getElementsByTagName('head')[0].appendChild(_link);
        _link.href = `tt-favicon-${v}x${v}.png`;
        // @ts-ignore
        _link.type = 'image/png';
        // @ts-ignore
        _link.sizes = `${v}x${v}`;
      });

      let _apple = document.createElement('link');
      // @ts-ignore
      _apple.rel = 'apple-touch-icon';
      document.getElementsByTagName('head')[0].appendChild(_apple);
      _apple.href = `tt-apple-touch-icon.png`;
      // @ts-ignore
      _apple.type = 'image/png';
    }
  }, []);

  useEffect(() => {
    // `linked=1` marks navigation into a linked catalog (guest tapped "Explore" or is
    // deep-linked/refreshed into one) -- see qr_redirect.build_linked_catalog_url on the
    // backend. The guest is browsing within an existing session, not starting a new one,
    // so the cart/order_type/table_number/manifest bootstrap below must be skipped.
    const linked = query.get('linked') === '1';
    if (query.get('r_id') != null) {
      if (!linked) {
        dispatch(clearCart());
        var arr = []; // Array to hold the keys
        // Iterate over localStorage and insert the keys that meet the condition into arr
        for (var i = 0; i < localStorage.length; i++) {
          if (localStorage.key(i).substring(0, 5) == 'form_') {
            arr.push(localStorage.key(i));
          }
        }
        // Iterate over arr and remove the items by key
        for (var i = 0; i < arr.length; i++) {
          localStorage.removeItem(arr[i]);
        }
      }
      if (!linked && query.get('table_number') != null) {
        localStorage.setItem('__tt_table_number', query.get('table_number'));
        dispatch(setLastTableNumber(query.get('table_number')));
      }
      if (!linked) {
        if (query.get('order_type') != null) {
          localStorage.setItem('__tt_dining_mode', query.get('order_type'));

          // @ts-ignore
          dispatch(changeOrderType(query.get('order_type') ?? 'dine_in'));
        } else {
          localStorage.setItem('__tt_dining_mode', 'dine_in');
          dispatch(changeOrderType('dine_in'));
        }
      }
      if (query.get('ordering_enabled') !== null && query.get('qr') !== null) {
        dispatch(
          setQRCodeOrderingStatus({
            [query.get('qr')]:
              query.get('ordering_enabled') === 'True' ||
              query.get('ordering_enabled') === 'true',
          }),
        );
      } else if (query.get('qr') !== null) {
        dispatch(
          setQRCodeOrderingStatus({
            [query.get('qr')]: true,
          }),
        );
      }
      if (query.get('menu_ids') !== null && query.get('qr') !== null) {
        const menu_ids = query.getAll('menu_ids');
        const qr = query.get('qr');
        let values = {};
        values[qr] = menu_ids.map(v => parseInt(v));
        dispatch(setQRMenuIds(values));
        dispatch(setQR(qr));
        if (!linked) {
          const manifestElement = document.getElementById('app-manifest');
          console.log(
            `${window.location.protocol}${window.location.host}${window.location.pathname}${window.location.search}`,
          );
          const manifestString = JSON.stringify({
            ...manifest,
            start_url: `https://${window.location.host}${window.location.pathname}${window.location.search}`,
          });
          const blob = new Blob([manifestString], {
            type: 'application/json',
          });
          const manifestURL = URL.createObjectURL(blob);
          document
            .querySelector('#app-manifest')
            .setAttribute('href', manifestURL);
          console.log(manifestElement);
        }
      } else if (query.get('qr') !== null && query.get('menu_ids') === null) {
        const qr = query.get('qr');
        let values = {};
        values[qr] = [];
        dispatch(setQRMenuIds(values));
        dispatch(setQR(qr));
      } else {
        dispatch(setQR(null));
      }
      if (query.get('categorized_view') !== null) {
        dispatch(
          setCategorizedView(
            query.get('categorized_view') === 'True' ||
              query.get('categorized_view') === 'true',
          ),
        );
      } else {
        dispatch(setCategorizedView(false));
      }
      if (query.get('menus_only') !== null) {
        dispatch(
          setMenusOnly(
            query.get('menus_only') === 'True' ||
              query.get('menus_only') === 'true',
          ),
        );
      } else {
        dispatch(setMenusOnly(false));
      }
      if (query.get('address_id')) {
        if (!isNaN(parseInt(query.get('address_id')))) {
          const address_id = parseInt(query.get('address_id'));
          dispatch(fetchQRCodeAddress(parseInt(query.get('r_id')), address_id));
        } else {
          dispatch(setQRAddress(null));
        }
      }
      if (query.get('sub') != null) {
        dispatch(setSubLinkId(query.get('sub')));
      } else {
        dispatch(setSubLinkId(null));
      }

      // --- "Explore linked catalogs" nav-stack bookkeeping ---
      // A plain (non-linked) load starts a fresh root; a linked=1 load is either a deep
      // link/refresh into an already-explored node (pop back to it -- cycle handling) or a
      // brand-new node reached from the current one (push it). Either way, resolve this
      // qr's own links so the "Explore" drawer/FAB can reflect them.
      const currentQr = query.get('qr');
      if (currentQr !== null) {
        const node = {
          qr: currentQr,
          r_id: parseInt(query.get('r_id')),
          name: query.get('name') ?? '',
          url: `${window.location.pathname}${window.location.search}`,
        };
        if (linked) {
          const alreadyOnStack = store
            .getState()
            .linkedNav.navStack.some(n => n.qr === currentQr);
          if (alreadyOnStack) {
            dispatch(popToNode(currentQr));
          } else {
            dispatch(pushNode(node));
          }
        } else {
          dispatch(resetStackToRoot(node));
        }
        qrLinksApi
          .getLinks(currentQr)
          .then(response => {
            dispatch(
              setLinksForQr({ qr: currentQr, links: response.data.links }),
            );
          })
          .catch(() => {});
      }

      setRUrl(
        encodeURIComponent(
          `https://order.tabletab.io/feed/restaurants/${query.get(
            'r_id',
          )}/menus`,
        ),
      );
    } else {
      setRUrl('https://order.tabletab.io/feed');
    }
  }, []);

  useEffect(() => {
    if (profile) {
      const themeString = localStorage.getItem(
        `__${profile?.restaurant_id}__theme`,
      );
      if (themeString) {
        try {
          const theme = JSON.parse(themeString);
          applyWithThemeObject(
            theme?.default_mode === 'dark' ? theme?.dark : theme?.light,
          );
          const element = document.querySelector('#app-theme-color');
          element.setAttribute(
            'content',
            theme?.default_mode === 'dark'
              ? RGBAToHexA(`${theme?.dark['background']}`)
              : RGBAToHexA(`${theme?.dark['background']}`),
          );
        } catch (e) {}
      }
    }
  }, [profile?.id]);

  useEffect(() => {
    let isMounted = true;
    if (
      localStorage.getItem('tabletabToken') !== undefined &&
      localStorage.getItem('tabletabToken') !== null
    ) {
      dispatch(
        getCurrentGuestProfile(() => {
          // fetchCurrentOpenOrder();
          setLoading(false);
        }),
      );
    }

    return function cleanup() {
      isMounted = false;
    };
  }, [isLoggedIn]);

  return (
    <div className="bg-background min-h-screen">
      <AppRouteComponent
        spinning={loading}
        onLoad={() => {}}
        render={() => (isLoggedIn ? <Navigate to={rUrl} /> : <Login />)}
        render1={() => <Navigate to={rUrl} />}
        render2={() => <Navigate to={rUrl} />}
        render3={() => {
          return <Navigate to={rUrl} />;
        }}
      />
    </div>
  );
}

const App = props => {
  return <AppRoute props={props} />;
};

export default App;
